import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  alpha,
  Divider,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { aiGradientButtonSx, getAiOverlayHeaderSx, getAiOverlayPaperSx, getAiOverlayShellSx, AI_OVERLAY_ACCENT, AI_OVERLAY_ACCENT_2 } from '../shared';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { extractWalkInIdDocument } from '../services/api';

const MAX_EDGE_CLIENT = 2048;
const JPEG_Q = 0.82;

/** Downscale on device before upload — fewer bytes, faster API, same extraction quality. */
function resizeImageToJpegBlob(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const longest = Math.max(width, height);
      if (longest > MAX_EDGE_CLIENT) {
        const r = MAX_EDGE_CLIENT / longest;
        width = Math.round(width * r);
        height = Math.round(height * r);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not available'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) reject(new Error('Could not process image'));
          else resolve(blob);
        },
        'image/jpeg',
        JPEG_Q,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Invalid image file'));
    };
    img.src = url;
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

async function fileToSlot(file) {
  const fileForResize =
    file instanceof File ? file : new File([file], 'image.jpg', { type: file.type || 'image/jpeg' });
  const jpegBlob = await resizeImageToJpegBlob(fileForResize);
  const dataUrl = await blobToDataUrl(jpegBlob);
  return { blob: jpegBlob, dataUrl };
}

/**
 * Full-area overlay for AI ID capture + Gemini extraction (server-side key).
 * Supports front + optional back (e.g. Aadhaar two-sided).
 */
function WalkInAiCheckinOverlay({ open, onClose, onSuccess, isDark, title = 'AI Check-in' }) {
  const uploadFrontRef = useRef(null);
  const uploadBackRef = useRef(null);
  const captureInputRef = useRef(null);
  const cameraTargetRef = useRef('front');
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [frontSlot, setFrontSlot] = useState(null);
  const [backSlot, setBackSlot] = useState(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  }, []);

  useEffect(() => {
    if (!open) {
      stopCamera();
      setError('');
      setLoading(false);
      setFrontSlot(null);
      setBackSlot(null);
    }
  }, [open, stopCamera]);

  useEffect(
    () => () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    },
    [],
  );

  const runExtraction = useCallback(async () => {
    if (!frontSlot?.blob) {
      setError('Add the front side of the ID (photo side).');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await extractWalkInIdDocument({
        front: frontSlot.blob,
        back: backSlot?.blob ?? null,
        frontFilename: 'id-front.jpg',
        backFilename: 'id-back.jpg',
      });
      if (!data?.success || !data.guest) {
        throw new Error(data?.error || 'Extraction failed');
      }
      onSuccess({
        ...data.guest,
        guest_phone: '',
        phone: '',
      }, {
        frontDataUrl: frontSlot.dataUrl,
        backDataUrl: backSlot?.dataUrl ?? null,
      });
    } catch (e) {
      const msg =
        e?.userMessage ||
        e?.response?.data?.error ||
        e?.message ||
        'Could not read this document';
      setError(typeof msg === 'string' ? msg : 'Could not read this document');
    } finally {
      setLoading(false);
    }
  }, [frontSlot, backSlot, onSuccess]);

  const onPickFront = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      setError('Please choose an image (JPEG, PNG, or WebP).');
      return;
    }
    setError('');
    try {
      setFrontSlot(await fileToSlot(file));
    } catch {
      setError('Could not load that image.');
    }
  };

  const onPickBack = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      setError('Please choose an image (JPEG, PNG, or WebP).');
      return;
    }
    setError('');
    try {
      setBackSlot(await fileToSlot(file));
    } catch {
      setError('Could not load that image.');
    }
  };

  const openCamera = async (side) => {
    cameraTargetRef.current = side;
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      captureInputRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 0);
    } catch {
      captureInputRef.current?.click();
    }
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      async (blob) => {
        stopCamera();
        if (!blob) return;
        const side = cameraTargetRef.current;
        try {
          const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
          const slot = await fileToSlot(file);
          if (side === 'back') setBackSlot(slot);
          else setFrontSlot(slot);
        } catch {
          setError('Could not save photo.');
        }
      },
      'image/jpeg',
      JPEG_Q,
    );
  };

  const onCaptureFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    const side = cameraTargetRef.current;
    try {
      const slot = await fileToSlot(file);
      if (side === 'back') setBackSlot(slot);
      else setFrontSlot(slot);
    } catch {
      setError('Could not load that image.');
    }
  };

  if (!open) return null;

  const accent = AI_OVERLAY_ACCENT;
  const accent2 = AI_OVERLAY_ACCENT_2;

  const sideRow = (label, required, slot, onUpload, onCamera, onClear) => (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {label}
          {required ? (
            <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>*</Typography>
          ) : (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
              (optional)
            </Typography>
          )}
        </Typography>
        {slot && (
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            label="Added"
            color="success"
            variant="outlined"
            onDelete={onClear}
          />
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          size="medium"
          startIcon={<CloudUploadIcon />}
          onClick={onUpload}
          disabled={loading}
          sx={{
            borderColor: alpha(accent, 0.5),
            color: accent,
          }}
        >
          Upload
        </Button>
        <Button
          fullWidth
          variant="outlined"
          size="medium"
          startIcon={<CameraAltIcon />}
          onClick={onCamera}
          disabled={loading}
          sx={{
            borderColor: alpha(accent2, 0.55),
            color: accent2,
          }}
        >
          Camera
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Box sx={getAiOverlayShellSx(isDark)}>
      <Paper elevation={0} sx={getAiOverlayPaperSx(isDark, accent, accent2)}>
        <Box sx={getAiOverlayHeaderSx(accent, accent2, isDark)}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <AutoAwesomeIcon sx={{ color: accent, filter: `drop-shadow(0 0 8px ${alpha(accent, 0.8)})` }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 0.3 }}>
              {title}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 10,
                bgcolor: alpha(accent2, 0.2),
                color: accent2,
                fontWeight: 600,
              }}
            >
              Gemini
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} aria-label="Close AI check-in">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <input
            ref={uploadFrontRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={onPickFront}
          />
          <input
            ref={uploadBackRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={onPickBack}
          />
          <input
            ref={captureInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={onCaptureFilePick}
          />

          {loading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.12),
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${accent} 0%, ${accent2} 100%)`,
                  },
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
                Reading document(s) with AI…
              </Typography>
            </Box>
          )}

          {!loading && cameraOpen && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="video"
                ref={videoRef}
                autoPlay
                playsInline
                muted
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  bgcolor: 'black',
                  maxHeight: 280,
                  objectFit: 'cover',
                }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="center">
                <Button variant="outlined" size="small" onClick={stopCamera}>
                  Cancel
                </Button>
                <Button variant="contained" size="small" onClick={capturePhoto} sx={{ background: `linear-gradient(90deg, ${accent}, ${accent2})` }}>
                  Capture
                </Button>
              </Stack>
            </Box>
          )}

          {!loading && !cameraOpen && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1.45 }}>
                Front required (photo side). Back optional for two-sided IDs — both sent in one request.
              </Typography>

              <Stack spacing={2}>
                {sideRow(
                  'Front side',
                  true,
                  frontSlot,
                  () => uploadFrontRef.current?.click(),
                  () => openCamera('front'),
                  () => setFrontSlot(null),
                )}
                <Divider flexItem sx={{ borderColor: alpha(accent, 0.15) }} />
                {sideRow(
                  'Back side',
                  false,
                  backSlot,
                  () => uploadBackRef.current?.click(),
                  () => openCamera('back'),
                  () => setBackSlot(null),
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !frontSlot}
                  startIcon={<AutoAwesomeIcon />}
                  onClick={runExtraction}
                  sx={aiGradientButtonSx({
                    mt: 1,
                    py: 1.35,
                  })}
                >
                  Read with AI & fill form
                </Button>
              </Stack>
            </>
          )}

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default WalkInAiCheckinOverlay;
